import { FocusEvent } from "react";
import { mergeRefs } from "react-merge-refs";
import { Button, Props } from "./Trigger.Parts";
import { MultiSelectContent } from "./multi-select-content/MultiSelectContent";
import { SingleSelectContent } from "./single-select-content/SingleSelectContent";

/**
 * Renders the base trigger button for both single and multi select components
 */
export const Trigger = ({
  name,
  label,
  id,
  placeholder,
  multi,
  search,
  width,
  maxWidth,
  rows = 1,
  disabled,
  hasError,
  subtle,
  small,
  onBlur,
  context,
  referenceProps,
  inputRef,
  buttonRef,
  setAnchorEl,
  referenceRef,
  isOpen,
  hasContent,
  selection,
  searchValue,
  activeIndex,
  filteredOptions,
  getLabelForValue,
  handleOpenChange,
  handleSelect,
  setSelection,
  clearSelection,
  clearable,
  visibleSelections,
  overflowCount,
  searchInputId,
  searchInput,
}: Props) => {
  // #region Handler Functions
  const handleTriggerClick = () => {
    if (disabled) return;
    if (search) {
      if ((isOpen && document.activeElement === inputRef.current) || isOpen) {
        handleOpenChange(false);
        inputRef.current?.blur();
      } else {
        handleOpenChange(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    } else handleOpenChange(!isOpen);
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    context.dataRef.current.isFocusVisible = true;

    if (typeof referenceProps.onKeyDown === "function")
      referenceProps.onKeyDown(e);

    switch (e.key) {
      case "Enter":
        if (activeIndex === null) {
          handleOpenChange(!isOpen);
          if (isOpen) setTimeout(() => inputRef.current?.blur(), 1);
        } else {
          e.preventDefault();
          handleSelect(activeIndex);
        }
        if (search) requestAnimationFrame(() => inputRef.current?.focus());
        break;
      case "Backspace":
        if (searchValue === "" && selection.length > 0) {
          e.preventDefault();
          const newSelection = selection.slice(0, -1);
          setSelection(newSelection);
        }
        break;
    }
  };

  const handleTriggerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (typeof referenceProps.onBlur === "function") referenceProps.onBlur(e);

    const currentTarget = e.currentTarget;

    requestAnimationFrame(() => {
      const activeElement = document.activeElement;

      // Focus left the trigger
      if (!currentTarget.contains(activeElement)) {
        handleOpenChange(false);

        const syntheticEvent = {
          ...e,
          currentTarget: buttonRef.current,
          target: buttonRef.current,
        } as FocusEvent<HTMLInputElement>;

        onBlur?.(syntheticEvent);
      }
    });
  };
  // #endregion

  return (
    <Button
      ref={mergeRefs([setAnchorEl, referenceRef, buttonRef])}
      {...referenceProps}
      tabIndex={search && isOpen ? -1 : 0}
      role="button"
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      onBlur={handleTriggerBlur}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      $multi={multi}
      $selection={Boolean(selection.length)}
      $active={isOpen}
      $width={width}
      $maxWidth={maxWidth}
      $rows={rows}
      $disabled={disabled}
      $hasError={hasError}
      $label={label}
      $subtle={subtle}
      $small={small}
      id={id}
    >
      {multi ? (
        <MultiSelectContent
          name={name}
          rows={rows}
          small={small}
          hasContent={hasContent}
          label={label}
          searchInputId={searchInputId}
          placeholder={placeholder}
          buttonRef={buttonRef}
          visibleSelections={visibleSelections}
          getLabelForValue={getLabelForValue}
          search={search}
          overflowCount={overflowCount}
          setSelection={setSelection}
          selection={selection}
          inputRef={inputRef}
          disabled={disabled}
          context={context}
          referenceId={id}
          filteredOptions={filteredOptions}
          activeIndex={activeIndex}
          searchInput={searchInput}
        />
      ) : (
        <SingleSelectContent
          name={name}
          rows={rows}
          hasContent={hasContent}
          label={label}
          searchInputId={searchInputId}
          placeholder={placeholder}
          selection={selection}
          getLabelForValue={getLabelForValue}
          search={search}
          clearSelection={clearSelection}
          clearable={clearable}
          buttonRef={buttonRef}
          disabled={disabled}
          context={context}
          referenceId={id}
          searchInput={searchInput}
        />
      )}
    </Button>
  );
};
